import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createWatchlistItem,
  deleteWatchlistItem,
  updateWatchlistLinks,
  partialUpdateWatchlistItem,
  fetchAccounts,
  makeItemGlobal,
} from '../../api/api';
import { getSymbolInfo } from '../../api/api';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Modal } from '../ui/modal';
import { useTrade } from '../../context/TradeProvider';
import { useWatchlistData } from '../../context/WatchlistContext';
import { getDigitsFor } from '../../utils/symbolDigitsStore';
import API_BASE_URL, { WS_BASE_URL } from "../../api/config";
import globalPriceManager from '../../utils/globalPriceManager';
import {
  Globe2,
  PencilLine,
  Plus,
  Trash2,
  X,
} from 'lucide-react';

// ---- Local caches (persisted in localStorage) ----
const prevCloseInflight = new Map(); // key: acc|sym|YYYY-MM-DD -> Promise<number|null>
const pipSizeInflight = new Map();   // key: acc|sym -> Promise<number|null>
const WATCHLIST_ENRICHMENT_CONCURRENCY = 4;

async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let index = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (index < items.length) {
      const current = index;
      index += 1;
      results[current] = await worker(items[current], current);
    }
  });
  await Promise.all(workers);
  return results;
}

const utcDateKey = (d = new Date()) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const nextUtcMidnightMs = () => {
  const now = new Date();
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
  return next.getTime();
};

const getPrevCloseCacheKey = (accId, sym, dateKey) => `watchlist:prevClose:${accId}:${sym}:${dateKey}`;
const getPipSizeCacheKey = (accId, sym) => `watchlist:pipSize:${accId}:${sym}`;

function getCachedPrevClose(accId, sym, dateKey) {
  try {
    const raw = localStorage.getItem(getPrevCloseCacheKey(accId, sym, dateKey));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj && Number.isFinite(obj.value) && Number.isFinite(obj.expiresAt) && obj.expiresAt > Date.now()) {
      return obj.value;
    }
  } catch { /* ignore */ }
  return null;
}
function setCachedPrevClose(accId, sym, dateKey, value) {
  try {
    const payload = { value, expiresAt: nextUtcMidnightMs() };
    localStorage.setItem(getPrevCloseCacheKey(accId, sym, dateKey), JSON.stringify(payload));
  } catch { /* ignore */ }
}

function getCachedPipSize(accId, sym) {
  try {
    const raw = localStorage.getItem(getPipSizeCacheKey(accId, sym));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj && Number.isFinite(obj.value) && Number.isFinite(obj.expiresAt) && obj.expiresAt > Date.now()) {
      return obj.value;
    }
  } catch { /* ignore */ }
  return null;
}
function setCachedPipSize(accId, sym, value) {
  try {
    // Long TTL (30 days) for pip size
    const ttlMs = 30 * 24 * 60 * 60 * 1000;
    const payload = { value, expiresAt: Date.now() + ttlMs };
    localStorage.setItem(getPipSizeCacheKey(accId, sym), JSON.stringify(payload));
  } catch { /* ignore */ }
}

function normalizeSymbol(symbol) {
  return String(symbol || '').trim().toUpperCase();
}

function formatDisplaySymbol(symbol) {
  const normalized = normalizeSymbol(symbol);
  const compact = normalized.replace(/\//g, '');
  if (/^[A-Z]{6}$/.test(compact)) return `${compact.slice(0, 3)}/${compact.slice(3)}`;
  return normalized;
}

const WATCHLIST_LAYOUT_OPTIONS = [
  { value: 'vertical', label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
];

const WATCHLIST_LAYOUT_VALUES = new Set(WATCHLIST_LAYOUT_OPTIONS.map((option) => option.value));

const FLAT_COLUMN_DEFAULTS = [84, 78, 62, 62, 28];
const FLAT_COLUMN_MINS = [58, 58, 48, 48, 28];
const FLAT_COLUMN_MAXES = [220, 180, 150, 150, 44];
const FLAT_ROW_HEIGHT = 32;
const FLAT_HEADER_HEIGHT = 28;

function normalizeWatchlistLayoutMode(mode) {
  return WATCHLIST_LAYOUT_VALUES.has(mode) ? mode : 'auto';
}

function normalizeFlatColumnWidths(widths) {
  if (!Array.isArray(widths)) return FLAT_COLUMN_DEFAULTS;
  return FLAT_COLUMN_DEFAULTS.map((fallbackWidth, index) => {
    const width = Number(widths[index]);
    if (!Number.isFinite(width)) return fallbackWidth;
    return Math.max(FLAT_COLUMN_MINS[index], Math.min(FLAT_COLUMN_MAXES[index], Math.round(width)));
  });
}

function useWatchlistInstrumentDisplay({ accountId, symbol, price, deltaPct, deltaPts }) {
  const digits = useMemo(() => {
    const cached = getDigitsFor?.(accountId, symbol);
    if (Number.isInteger(cached)) return cached;
    const currentSymbol = symbol || '';
    if (/JPY/i.test(currentSymbol)) return 3;
    if (/XAG|SILV|XBR|XTI|WTI|BRENT|USOIL|UKOIL/i.test(currentSymbol)) return 3;
    if (/\b(US30|US100|US500|GER40|UK100|HK50)\b/i.test(currentSymbol)) return 2;
    return 5;
  }, [accountId, symbol]);

  const hasLivePrice = Number.isFinite(Number(price));
  const hasRealChange = Number.isFinite(deltaPct) && Number.isFinite(deltaPts);
  const isUp = hasRealChange ? Number(deltaPct) >= 0 : true;
  const accentColor = hasRealChange ? (isUp ? '41 180 132' : '236 72 153') : '71 170 255';

  const displayPrice = useMemo(() => {
    if (!hasLivePrice) return '--';
    const value = Number(price);
    try {
      return value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    } catch {
      return value.toFixed(digits);
    }
  }, [digits, hasLivePrice, price]);

  const changeLabel = hasRealChange
    ? `${Number(deltaPct) > 0 ? '+' : ''}${Number(deltaPct).toFixed(2)}%`
    : 'Pending';

  const pointsLabel = useMemo(() => {
    if (!hasRealChange) return '--';
    const value = Number(deltaPts);
    try {
      return `${value >= 0 ? '+' : ''}${value.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`;
    } catch {
      return `${value >= 0 ? '+' : ''}${value.toFixed(Math.min(5, Math.max(2, digits ?? 4)))}`;
    }
  }, [deltaPts, digits, hasRealChange]);

  const displaySymbol = useMemo(() => formatDisplaySymbol(symbol), [symbol]);

  return {
    hasRealChange,
    isUp,
    accentColor,
    displayPrice,
    changeLabel,
    pointsLabel,
    displaySymbol,
  };
}

/* ---------- Studio pill card ---------- */
function WatchlistPill3D({
  accountId,
  symbol,
  price,
  deltaPct,
  deltaPts,
  isActive = false,
  onClick,
  onEdit,
  onRemove,
  layoutVariant = 'horizontal',
  className = '',
}) {
  const {
    hasRealChange,
    isUp,
    accentColor,
    displayPrice,
    changeLabel,
    pointsLabel,
    displaySymbol,
  } = useWatchlistInstrumentDisplay({ accountId, symbol, price, deltaPct, deltaPts });
  const isVertical = layoutVariant === 'vertical';

  const changeToneClasses = hasRealChange
    ? (isUp ? 'watchlist-delta-pill watchlist-delta-pill--up' : 'watchlist-delta-pill watchlist-delta-pill--down')
    : 'watchlist-delta-pill watchlist-delta-pill--neutral';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
      title={`Set ${symbol} as active`}
      aria-pressed={isActive}
      data-tone={hasRealChange ? (isUp ? 'up' : 'down') : 'neutral'}
      className={`watchlist-pill group relative h-[58px] w-full overflow-hidden rounded-[18px] px-3 py-2 text-left transition duration-200 hover:-translate-y-0.5 focus-visible:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isActive ? 'watchlist-pill--active' : ''} ${isVertical ? 'watchlist-pill--row' : 'watchlist-pill--compact'} ${className}`}
      style={{ '--watchlist-accent': accentColor }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden />

      <div className="watchlist-soft-glow pointer-events-none absolute right-2 top-2 h-12 w-12 rounded-full blur-2xl" aria-hidden />

      <div className="absolute right-1.5 top-1.5 z-20 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        {onEdit && (
          <button
            type="button"
            aria-label="Edit watchlist item"
            className="watchlist-icon-button flex h-6 w-6 items-center justify-center rounded-full border transition"
            onClick={(event) => { event.stopPropagation(); onEdit(); }}
          >
            <PencilLine className="h-3.5 w-3.5" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            aria-label="Remove watchlist item"
            className="watchlist-icon-button watchlist-icon-button--danger flex h-6 w-6 items-center justify-center rounded-full border transition"
            onClick={(event) => { event.stopPropagation(); onRemove(); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className={`relative z-10 flex h-full flex-col justify-center ${isVertical ? 'gap-1.5' : 'gap-1'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-1.5">
            <div className="truncate text-[13px] font-semibold tracking-[0.04em] text-textPrimary">
              {displaySymbol}
            </div>
            {isActive && (
              <span className="watchlist-focus-badge shrink-0 rounded-full border px-2 py-1 text-[9px] font-medium uppercase tracking-[0.18em]">
                Focus
              </span>
            )}
          </div>

          <div className="shrink-0 font-mono text-[13px] leading-none text-textPrimary">
            {displayPrice}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className={`min-w-0 truncate text-[10px] ${hasRealChange ? (isUp ? 'watchlist-market-up' : 'watchlist-market-down') : 'watchlist-market-neutral'}`}>
            {pointsLabel}
          </div>

          <div className={`inline-flex shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${changeToneClasses}`}>
            {changeLabel}
          </div>
        </div>
      </div>
    </div>
  );
}

function WatchlistFlatRow({
  accountId,
  symbol,
  price,
  deltaPct,
  deltaPts,
  isActive = false,
  onClick,
  onEdit,
  onRemove,
  className = '',
}) {
  const {
    hasRealChange,
    isUp,
    displayPrice,
    changeLabel,
    pointsLabel,
    displaySymbol,
  } = useWatchlistInstrumentDisplay({ accountId, symbol, price, deltaPct, deltaPts });

  const toneClass = hasRealChange
    ? (isUp ? 'watchlist-market-up' : 'watchlist-market-down')
    : 'watchlist-market-neutral';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
      title={`Set ${symbol} as active`}
      aria-pressed={isActive}
      className={`watchlist-flat-row watchlist-flat-grid group grid h-8 items-center gap-2 px-2 text-xs transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${isActive ? 'watchlist-flat-row--active' : ''} ${className}`}
    >
      <div className="min-w-0 truncate font-semibold text-textPrimary">{displaySymbol}</div>
      <div className="truncate text-right font-mono text-textPrimary">{displayPrice}</div>
      <div className={`truncate text-right font-mono ${toneClass}`}>{pointsLabel}</div>
      <div className={`truncate text-right font-mono ${toneClass}`}>{changeLabel}</div>
      <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        {onEdit && (
          <button
            type="button"
            aria-label="Edit watchlist item"
            className="watchlist-icon-button flex h-6 w-6 items-center justify-center rounded border transition"
            onClick={(event) => { event.stopPropagation(); onEdit(); }}
          >
            <PencilLine className="h-3.5 w-3.5" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            aria-label="Remove watchlist item"
            className="watchlist-icon-button watchlist-icon-button--danger flex h-6 w-6 items-center justify-center rounded border transition"
            onClick={(event) => { event.stopPropagation(); onRemove(); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function WatchlistCompactTile({
  accountId,
  symbol,
  price,
  deltaPct,
  deltaPts,
  isActive = false,
  onClick,
  onEdit,
  onRemove,
}) {
  const {
    hasRealChange,
    isUp,
    displayPrice,
    changeLabel,
    displaySymbol,
  } = useWatchlistInstrumentDisplay({ accountId, symbol, price, deltaPct, deltaPts });

  const toneClass = hasRealChange
    ? (isUp ? 'watchlist-market-up' : 'watchlist-market-down')
    : 'watchlist-market-neutral';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
      title={`Set ${symbol} as active`}
      aria-pressed={isActive}
      className={`watchlist-compact-tile group relative flex h-8 items-center gap-1.5 overflow-hidden rounded-lg border px-2 text-xs transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${isActive ? 'watchlist-compact-tile--active' : ''}`}
    >
      <span className="shrink-0 whitespace-nowrap font-semibold text-textPrimary">{displaySymbol}</span>
      <span className="w-[56px] shrink-0 truncate text-right font-mono text-textPrimary">{displayPrice}</span>
      <span className={`w-[42px] shrink-0 truncate text-right font-mono text-[10px] ${toneClass}`}>{changeLabel}</span>
      <span className="absolute right-1 top-1 hidden gap-1 group-hover:flex group-focus-within:flex">
        {onEdit && (
          <button
            type="button"
            aria-label="Edit watchlist item"
            className="watchlist-icon-button flex h-5 w-5 items-center justify-center rounded border transition"
            onClick={(event) => { event.stopPropagation(); onEdit(); }}
          >
            <PencilLine className="h-3 w-3" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            aria-label="Remove watchlist item"
            className="watchlist-icon-button watchlist-icon-button--danger flex h-5 w-5 items-center justify-center rounded border transition"
            onClick={(event) => { event.stopPropagation(); onRemove(); }}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </span>
    </div>
  );
}

/* ---------- Main Widget ---------- */
export default function WatchlistWidget({
  accountId,
  restrictToAccount = false,
  layoutMode = 'auto',
  onLayoutModeChange,
  flatColumnWidths,
  onFlatColumnWidthsChange,
  instrumentStyle = 'pill',
  isCustomizing = false,
}) {
  const { setTradeContext, timeframe, accountId: selectedAccountId, symbol: activeSymbol } = useTrade();
  const { refresh: refreshWatchlist } = useWatchlistData();

  // data
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);

  // ui state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localLayoutMode, setLocalLayoutMode] = useState(() => normalizeWatchlistLayoutMode(layoutMode));
  const [localFlatColumnWidths, setLocalFlatColumnWidths] = useState(() => normalizeFlatColumnWidths(flatColumnWidths));
  const flatColumnWidthsRef = useRef(localFlatColumnWidths);

  // filter
  const [activeAccountFilter, setActiveAccountFilter] = useState('all'); // 'all' or accountId

  // add instrument modal state
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [instrument, setInstrument] = useState('');
  // Exchange removed from form
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // edit links (per tile)
  const [editingId, setEditingId] = useState(null);

  // live prices
  const [prices, setPrices] = useState({}); // { [symbol]: { bid, ask } }

  // previous D1 close per symbol for daily change
  const [prevCloseBySymbol, setPrevCloseBySymbol] = useState({});
  const [_pipSizeBySymbol, setPipSizeBySymbol] = useState({});

  // container ref for optional measurements
  const listAreaRef = useRef(null);
  const pillsRef = useRef(null);

  // UI metrics: measured gaps and scrollbar gutter for precise edge spacing
  const [uiMetrics, setUiMetrics] = useState({ gapX: 8, gapY: 8, scrollGutter: 0 });
  const [surfaceMetrics, setSurfaceMetrics] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setLocalLayoutMode(normalizeWatchlistLayoutMode(layoutMode));
  }, [layoutMode]);

  useEffect(() => {
    const normalizedWidths = normalizeFlatColumnWidths(flatColumnWidths);
    flatColumnWidthsRef.current = normalizedWidths;
    setLocalFlatColumnWidths(normalizedWidths);
  }, [flatColumnWidths]);

  useEffect(() => {
    const measure = () => {
      try {
        const root = listAreaRef.current;
        const pills = pillsRef.current;
        let gapX = 8, gapY = 8, gutter = 0;
        if (pills && typeof window !== 'undefined') {
          const cs = window.getComputedStyle(pills);
          const gx = parseFloat(cs.columnGap || cs.gap || '8');
          const gy = parseFloat(cs.rowGap || cs.gap || '8');
          if (Number.isFinite(gx)) gapX = gx;
          if (Number.isFinite(gy)) gapY = gy;
        }
        if (root) {
          const ow = root.offsetWidth || 0;
          const cw = root.clientWidth || 0;
          gutter = Math.max(0, ow - cw);
        }
        setUiMetrics({ gapX, gapY, scrollGutter: gutter });
      } catch { /* ignore */ }
    };
    measure();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
      // slight delay to catch late layout (fonts/scrollbar appearance)
      const t = setTimeout(measure, 50);
      return () => { window.removeEventListener('resize', measure); clearTimeout(t); };
    }
  }, []);

  // Re-measure when filters open/close or items count changes (scrollbar may change)
  useEffect(() => {
    const r = listAreaRef.current;
    if (!r) return;
    const measure = () => {
      try {
        const pills = pillsRef.current;
        let gapX = uiMetrics.gapX, gapY = uiMetrics.gapY, gutter = 0;
        if (pills && typeof window !== 'undefined') {
          const cs = window.getComputedStyle(pills);
          const gx = parseFloat(cs.columnGap || cs.gap || `${gapX}`);
          const gy = parseFloat(cs.rowGap || cs.gap || `${gapY}`);
          if (Number.isFinite(gx)) gapX = gx;
          if (Number.isFinite(gy)) gapY = gy;
        }
        const ow = r.offsetWidth || 0;
        const cw = r.clientWidth || 0;
        gutter = Math.max(0, ow - cw);
        setUiMetrics({ gapX, gapY, scrollGutter: gutter });
      } catch { /* ignore */ }
    };
    measure();
    // observe size changes of the scroll container
    const ro = new ResizeObserver(() => measure());
    ro.observe(r);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  useEffect(() => {
    const root = listAreaRef.current;
    if (!root) return;
    const measureSurface = () => {
      setSurfaceMetrics({
        width: root.clientWidth || 0,
        height: root.clientHeight || 0,
      });
    };
    measureSurface();
    const ro = new ResizeObserver(() => measureSurface());
    ro.observe(root);
    return () => ro.disconnect();
  }, [items.length, showAddPanel, editingId]);

  /* -------- data loaders -------- */
  const loadAccounts = async () => {
    try {
      const resp = await fetchAccounts();
      setAccounts(Array.isArray(resp) ? resp : resp?.data || []);
    } catch (e) {
      console.error('WatchlistWidget: fetchAccounts failed', e);
      setAccounts([]);
    }
  };

  const loadItems = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      let arr = await refreshWatchlist({ force });
      arr = Array.isArray(arr) ? arr : [];
      // Client-side fallback filtering: ensure only items linked to target account are shown
      const targetAcc = (restrictToAccount && accountId) ? accountId : (activeAccountFilter !== 'all' ? activeAccountFilter : null);
      if (targetAcc) {
        arr = arr.filter((it) => {
          // Always keep global / link-all items visible regardless of account filter
          if (it?.is_global || it?.link_all_accounts) return true;
          const accs = it?.accounts;
          if (Array.isArray(accs)) {
            if (accs.length > 0 && typeof accs[0] === 'string') return accs.includes(targetAcc);
            return accs.some((a) => a?.id === targetAcc);
          }
          const ids = it?.account_ids;
          return Array.isArray(ids) ? ids.includes(targetAcc) : false;
        });
      }
      setItems(arr);
    } catch (e) {
      setItems([]);
      setError(e?.message || 'Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }, [restrictToAccount, accountId, activeAccountFilter, refreshWatchlist]);

  useEffect(() => { loadAccounts(); }, []);
  // When restricted, default to current account; update when account changes
  useEffect(() => {
    if (restrictToAccount && accountId) {
      setActiveAccountFilter(accountId);
    }
  }, [restrictToAccount, accountId]);
  useEffect(() => { loadItems(); }, [loadItems]);

  /* -------- live prices wiring via global manager (connection persists across navigation) -------- */
  useEffect(() => {
    // NOTE: removed setPrices({}) to avoid wiping valid prices on re-subscription

    // Helper: choose the best account for a given item according to rules
    const normalizeLinkedIds = (it) => {
      const accs = it?.accounts;
      if (Array.isArray(accs)) {
        if (accs.length > 0 && typeof accs[0] === 'string') return accs.filter(Boolean);
        return accs.map((a) => a?.id).filter(Boolean);
      }
      const ids = it?.account_ids;
      return Array.isArray(ids) ? ids.filter(Boolean) : [];
    };

    const chooseAccountForItem = (item) => {
      const preferred = (activeAccountFilter !== 'all' ? activeAccountFilter : (accountId || selectedAccountId)) || null;
      const linkedIds = normalizeLinkedIds(item);
      const linksAll = !!item?.is_global || !!item?.link_all_accounts;
      if (preferred && (linksAll || linkedIds.includes(preferred))) return preferred;
      if (linkedIds.length > 0) return linkedIds[0];
      return accounts?.[0]?.id || preferred || null;
    };

    // Compute WS origin once
    let wsOrigin = '';
    if (WS_BASE_URL && typeof WS_BASE_URL === 'string' && WS_BASE_URL.length > 0) {
      wsOrigin = WS_BASE_URL.replace(/\/$/, '');
    } else if (API_BASE_URL) {
      try {
        const tmp = new URL(API_BASE_URL);
        const proto = tmp.protocol === 'https:' ? 'wss' : 'ws';
        wsOrigin = `${proto}://${tmp.host}`;
      } catch {
        // fallthrough to window.location
      }
    }
    if (!wsOrigin && typeof window !== 'undefined') {
      wsOrigin = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    }

    const unsubs = [];

    items.forEach((item) => {
      const symbol = item.instrument;
      if (!symbol) return;
      const accForItem = chooseAccountForItem(item);
      if (!accForItem) return;

      const unsub = globalPriceManager.subscribe(symbol, accForItem, timeframe || 'M15', wsOrigin, ({ bid, ask }) => {
        setPrices((prev) => {
          const prevEntry = prev[symbol] || {};
          return { ...prev, [symbol]: { bid: bid ?? prevEntry.bid, ask: ask ?? prevEntry.ask } };
        });
      });
      unsubs.push(unsub);
    });

    return () => {
      // Unsubscribe callbacks, but global connections stay alive
      unsubs.forEach((unsub) => unsub());
    };
  }, [items, activeAccountFilter, accountId, selectedAccountId, accounts, timeframe]);

  /* -------- previous close (D1) loader for daily change -------- */
  useEffect(() => {
    let alive = true;

    const normalizeLinkedIds = (it) => {
      const accs = it?.accounts;
      if (Array.isArray(accs)) {
        if (accs.length > 0 && typeof accs[0] === 'string') return accs.filter(Boolean);
        return accs.map((a) => a?.id).filter(Boolean);
      }
      const ids = it?.account_ids;
      return Array.isArray(ids) ? ids.filter(Boolean) : [];
    };

    const chooseAccountForItem = (item) => {
      const preferred = (activeAccountFilter !== 'all' ? activeAccountFilter : (accountId || selectedAccountId)) || null;
      const linkedIds = normalizeLinkedIds(item);
      const linksAll = !!item?.is_global || !!item?.link_all_accounts;
      if (preferred && (linksAll || linkedIds.includes(preferred))) return preferred;
      if (linkedIds.length > 0) return linkedIds[0];
      return accounts?.[0]?.id || preferred || null;
    };

    const fetchPrevClose = async (accId, sym) => {
      const dateKey = utcDateKey();
      // Try cache first
      const cached = getCachedPrevClose(accId, sym, dateKey);
      if (cached != null) return cached;
      // De-duplicate in-flight
      const inflightKey = `${accId}|${sym}|${dateKey}`;
      if (prevCloseInflight.has(inflightKey)) return prevCloseInflight.get(inflightKey);
      const prom = (async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) return null;
          const params = new URLSearchParams({ account_id: accId, symbol: sym, resolution: 'D1', count: '3', prefer_warehouse: '1' });
          // API_BASE_URL is already a full origin (e.g. http://host:port) from config
          const base = API_BASE_URL.replace(/\/$/, '');
          const resp = await fetch(`${base}/api/price/chart/history/?${params.toString()}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!resp.ok) return null;
          const data = await resp.json();
          const raw = Array.isArray(data?.candles) ? data.candles : [];
          const toSec = (t) => {
            if (t == null) return undefined;
            if (typeof t === 'number') return t > 1000000000000 ? Math.floor(t / 1000) : t;
            const ms = Date.parse(t);
            return Number.isNaN(ms) ? undefined : Math.floor(ms / 1000);
          };
          const candles = raw
            .map(d => ({ time: toSec(d.time), open: d.open, high: d.high, low: d.low, close: d.close }))
            .filter(d => typeof d.time === 'number' && Number.isFinite(d.close))
            .sort((a, b) => a.time - b.time);
          if (candles.length === 0) return null;
          const last = candles[candles.length - 1];
          const now = new Date();
          const toUTC = (sec) => { const dt = new Date(sec * 1000); return [dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()]; };
          const [ly, lm, ld] = toUTC(last.time);
          const [ny, nm, nd] = [now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()];
          const lastIsToday = ly === ny && lm === nm && ld === nd;
          const prev = lastIsToday && candles.length > 1 ? candles[candles.length - 2] : last;
          const prevClose = Number(prev?.close);
          const value = Number.isFinite(prevClose) ? prevClose : null;
          if (value != null) setCachedPrevClose(accId, sym, dateKey, value);
          return value;
        } catch {
          return null;
        } finally {
          prevCloseInflight.delete(inflightKey);
        }
      })();
      prevCloseInflight.set(inflightKey, prom);
      return prom;
    };

    const run = async () => {
      if (!Array.isArray(items) || items.length === 0) {
        if (alive) setPrevCloseBySymbol({});
        return;
      }
      const results = await mapWithConcurrency(items, WATCHLIST_ENRICHMENT_CONCURRENCY, async (item) => {
        const sym = item?.instrument;
        if (!sym) return null;
        const acc = chooseAccountForItem(item);
        if (!acc) return null;
        const pc = await fetchPrevClose(acc, sym);
        return pc != null ? [sym, pc] : null;
      });
      if (!alive) return;
  const next = {};
      for (const r of results) if (r && r[0]) next[r[0]] = r[1];
  // Merge to preserve any existing cached values for other symbols
  setPrevCloseBySymbol((prev) => ({ ...prev, ...next }));
    };

    run();
    return () => { alive = false; };
  }, [items, activeAccountFilter, accountId, selectedAccountId, accounts]);

  /* -------- pip size loader (per symbol) -------- */
  useEffect(() => {
    let alive = true;

    const normalizeLinkedIds = (it) => {
      const accs = it?.accounts;
      if (Array.isArray(accs)) {
        if (accs.length > 0 && typeof accs[0] === 'string') return accs.filter(Boolean);
        return accs.map((a) => a?.id).filter(Boolean);
      }
      const ids = it?.account_ids;
      return Array.isArray(ids) ? ids.filter(Boolean) : [];
    };

    const chooseAccountForItem = (item) => {
      const preferred = (activeAccountFilter !== 'all' ? activeAccountFilter : (accountId || selectedAccountId)) || null;
      const linkedIds = normalizeLinkedIds(item);
      const linksAll = !!item?.is_global || !!item?.link_all_accounts;
      if (preferred && (linksAll || linkedIds.includes(preferred))) return preferred;
      if (linkedIds.length > 0) return linkedIds[0];
      return accounts?.[0]?.id || preferred || null;
    };

    const run = async () => {
      if (!Array.isArray(items) || items.length === 0) {
        if (alive) setPipSizeBySymbol({});
        return;
      }
      const results = await mapWithConcurrency(items, WATCHLIST_ENRICHMENT_CONCURRENCY, async (item) => {
        const sym = item?.instrument;
        if (!sym) return null;
        const acc = chooseAccountForItem(item);
        if (!acc) return null;
        // Try cached
        const cached = getCachedPipSize(acc, sym);
        if (cached != null) return [sym, cached];
        const inflightKey = `${acc}|${sym}`;
        if (!pipSizeInflight.has(inflightKey)) {
          const prom = (async () => {
            try {
              const info = await getSymbolInfo(acc, sym);
              const pip = Number(info?.pip_size);
              if (pip && pip > 0) {
                setCachedPipSize(acc, sym, pip);
                return pip;
              }
              return null;
            } catch {
              return null;
            } finally {
              pipSizeInflight.delete(inflightKey);
            }
          })();
          pipSizeInflight.set(inflightKey, prom);
        }
        const pip = await pipSizeInflight.get(inflightKey);
        return (pip && pip > 0) ? [sym, pip] : null;
      });
      if (!alive) return;
      const next = {};
      for (const r of results) if (r && r[0]) next[r[0]] = r[1];
      setPipSizeBySymbol((prev) => ({ ...prev, ...next }));
    };

    run();
    return () => { alive = false; };
  }, [items, activeAccountFilter, accountId, selectedAccountId, accounts]);

  /* -------- handlers -------- */
  const closeAddPanel = useCallback(() => {
    setShowAddPanel(false);
    setInstrument('');
    setSelectedAccountIds([]);
  }, []);

  const addItem = async () => {
    if (!instrument) { setError('Instrument is required'); return; }
    const cleanedIds = (selectedAccountIds || []).filter((x) => x != null && x !== '');
    setLoading(true); setError(null);
    try {
      const linkAll = accounts.length > 0 && cleanedIds.length === accounts.length;
      const payload = linkAll
        ? { instrument, link_all_accounts: true }
        : (cleanedIds.length > 0
            ? { instrument, account_ids: cleanedIds }
            : { instrument });

      await createWatchlistItem(payload);
      closeAddPanel();
      await loadItems({ force: true });
    } catch (e) {
      // Fallback: if creation fails because instrument exists, try patching existing item
      try {
        const allItemsArray = await refreshWatchlist({ force: true });
        const existing = allItemsArray.find((x) => (x?.instrument || '').toLowerCase() === instrument.toLowerCase());
        if (existing) {
          const linkAll = accounts.length > 0 && cleanedIds.length === accounts.length;
          if (linkAll) {
            await partialUpdateWatchlistItem(existing.id, { link_all_accounts: true });
          } else {
            await updateWatchlistLinks(existing.id, cleanedIds);
          }
          closeAddPanel();
          await loadItems({ force: true });
          return;
        }
      } catch {
        // ignore and surface original error
      }
      setError(e?.message || 'Failed to add');
    } finally { setLoading(false); }
  };

  const removeItem = async (id) => {
    setLoading(true);
    try { await deleteWatchlistItem(id); await loadItems({ force: true }); }
    catch (e) { setError(e?.message || 'Failed to delete'); }
    finally { setLoading(false); }
  };

  const beginEdit = (item) => {
    setEditingId(item.id);
    const linked = Array.isArray(item?.accounts)
      ? (typeof item.accounts[0] === 'string' ? item.accounts.filter(Boolean) : item.accounts.map((a) => a?.id).filter(Boolean))
      : (Array.isArray(item?.account_ids) ? item.account_ids.filter(Boolean) : []);
    setSelectedAccountIds(linked);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setLoading(true);
    try {
      // Replace links entirely (empty array makes it user-general)
      const cleanedIds = (selectedAccountIds || []).filter((x) => x != null && x !== '');
      const linkAll = accounts.length > 0 && cleanedIds.length === accounts.length;
      if (linkAll) {
        await partialUpdateWatchlistItem(editingId, { link_all_accounts: true });
      } else {
        await updateWatchlistLinks(editingId, cleanedIds);
      }
      setEditingId(null);
      setSelectedAccountIds([]);
      await loadItems({ force: true });
    } catch (e) {
      setError(e?.message || 'Failed to update links');
    } finally {
      setLoading(false);
    }
  };

  // Default-select all accounts when opening the Add panel
  useEffect(() => {
    if (showAddPanel && accounts?.length > 0 && selectedAccountIds.length === 0) {
      if (restrictToAccount && accountId) {
        setSelectedAccountIds([accountId]);
        return;
      }
      setSelectedAccountIds(accounts.map((a) => a.id));
    }
  }, [showAddPanel, accounts, selectedAccountIds.length, restrictToAccount, accountId]);

  const requestedLayoutMode = normalizeWatchlistLayoutMode(localLayoutMode);
  const resolvedLayoutMode = useMemo(() => {
    if (requestedLayoutMode !== 'auto') return requestedLayoutMode;
    const width = surfaceMetrics.width;
    const height = surfaceMetrics.height;
    if (width > 0 && (width < 260 || (width < 340 && height > width * 1.05))) {
      return 'vertical';
    }
    return 'horizontal';
  }, [requestedLayoutMode, surfaceMetrics.height, surfaceMetrics.width]);

  const isFlatStyle = instrumentStyle === 'flat';
  const isFlushFlat = isFlatStyle;
  const isVerticalLayout = resolvedLayoutMode === 'vertical';
  const isFlatHorizontalTiles = isFlatStyle && requestedLayoutMode === 'horizontal' && !isVerticalLayout;
  const handleLayoutModeSelect = useCallback((nextMode) => {
    const normalizedMode = normalizeWatchlistLayoutMode(nextMode);
    setLocalLayoutMode(normalizedMode);
    onLayoutModeChange?.(normalizedMode);
  }, [onLayoutModeChange]);

  const startFlatColumnResize = useCallback((columnIndex, event) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidths = [...flatColumnWidthsRef.current];

    const handlePointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidths = [...startWidths];
      const firstWidth = Math.max(
        FLAT_COLUMN_MINS[columnIndex],
        Math.min(FLAT_COLUMN_MAXES[columnIndex], startWidths[columnIndex] + delta)
      );
      const consumedDelta = firstWidth - startWidths[columnIndex];
      const nextIndex = columnIndex + 1;

      nextWidths[columnIndex] = Math.round(firstWidth);

      if (nextIndex < FLAT_COLUMN_DEFAULTS.length - 1) {
        nextWidths[nextIndex] = Math.round(Math.max(
          FLAT_COLUMN_MINS[nextIndex],
          Math.min(FLAT_COLUMN_MAXES[nextIndex], startWidths[nextIndex] - consumedDelta)
        ));
      }

      flatColumnWidthsRef.current = nextWidths;
      setLocalFlatColumnWidths(nextWidths);
    };

    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      onFlatColumnWidthsChange?.(flatColumnWidthsRef.current);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [onFlatColumnWidthsChange]);

  const pillsLayoutStyle = useMemo(() => {
    const baseStyle = {
      paddingRight: `${uiMetrics.gapX + uiMetrics.scrollGutter}px`,
      paddingBottom: `${uiMetrics.gapY}px`,
      alignContent: 'start',
    };

    if (isVerticalLayout) {
      return {
        ...baseStyle,
        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
      };
    }

    const minColumnWidth = surfaceMetrics.width >= 860 ? 164 : surfaceMetrics.width >= 620 ? 152 : 136;
    return {
      ...baseStyle,
      gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}px, 1fr))`,
    };
  }, [isVerticalLayout, surfaceMetrics.width, uiMetrics.gapX, uiMetrics.gapY, uiMetrics.scrollGutter]);

  const flatColumnVars = useMemo(() => ({
    '--watchlist-flat-symbol': `${localFlatColumnWidths[0]}px`,
    '--watchlist-flat-last': `${localFlatColumnWidths[1]}px`,
    '--watchlist-flat-change': `${localFlatColumnWidths[2]}px`,
    '--watchlist-flat-change-pct': `${localFlatColumnWidths[3]}px`,
    '--watchlist-flat-actions': `${localFlatColumnWidths[4]}px`,
  }), [localFlatColumnWidths]);

  const flatRowsPerSection = useMemo(() => {
    if (!isFlatStyle || isVerticalLayout) return items.length || 1;
    const usableHeight = Math.max(FLAT_ROW_HEIGHT, surfaceMetrics.height - FLAT_HEADER_HEIGHT - uiMetrics.gapY);
    return Math.max(1, Math.floor(usableHeight / FLAT_ROW_HEIGHT));
  }, [isFlatStyle, isVerticalLayout, items.length, surfaceMetrics.height, uiMetrics.gapY]);

  const flatSections = useMemo(() => {
    if (!isFlatStyle || items.length === 0) return [];
    if (isVerticalLayout) return [items];
    const chunkSize = Math.max(1, flatRowsPerSection);
    return Array.from({ length: Math.ceil(items.length / chunkSize) }, (_, sectionIndex) =>
      items.slice(sectionIndex * chunkSize, sectionIndex * chunkSize + chunkSize)
    ).filter((sectionItems) => sectionItems.length > 0);
  }, [flatRowsPerSection, isFlatStyle, isVerticalLayout, items]);

  const flatSectionMinWidth = useMemo(
    () => Math.max(260, localFlatColumnWidths.reduce((total, width) => total + width, 0) + 48),
    [localFlatColumnWidths]
  );

  const instrumentsLayoutStyle = useMemo(() => {
    if (!isFlatStyle) return pillsLayoutStyle;
    if (isFlatHorizontalTiles) {
      return {
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${uiMetrics.gapY}px ${uiMetrics.gapX}px`,
        paddingRight: `${uiMetrics.gapX + uiMetrics.scrollGutter}px`,
        paddingBottom: `${uiMetrics.gapY}px`,
        alignContent: 'flex-start',
      };
    }
    const flatSectionCount = Math.max(1, flatSections.length);

    return {
      ...flatColumnVars,
      paddingRight: `${uiMetrics.gapX + uiMetrics.scrollGutter}px`,
      paddingBottom: `${uiMetrics.gapY}px`,
      alignContent: 'start',
      gridTemplateColumns: !isVerticalLayout && flatSectionCount > 1
        ? `repeat(${flatSectionCount}, minmax(${flatSectionMinWidth}px, 1fr))`
        : 'repeat(1, minmax(0, 1fr))',
    };
  }, [flatColumnVars, flatSectionMinWidth, flatSections.length, isFlatHorizontalTiles, isFlatStyle, isVerticalLayout, pillsLayoutStyle, uiMetrics.gapX, uiMetrics.gapY, uiMetrics.scrollGutter]);

  const getInstrumentRenderData = (item) => {
    const symbol = item.instrument;
    const quote = prices[symbol] || {};
    const prevClose = prevCloseBySymbol[symbol];
    let dailyPct;
    let dailyPoints;

    if (Number.isFinite(prevClose) && Number.isFinite(quote?.bid)) {
      const diff = Number(quote.bid) - Number(prevClose);
      dailyPoints = diff;
      dailyPct = prevClose !== 0 ? (diff / Number(prevClose)) * 100 : undefined;
    }

    return { symbol, quote, dailyPct, dailyPoints };
  };
  const denseHeader = isFlatStyle;
  const showLayoutControls = isCustomizing || denseHeader;

  /* -------- UI -------- */
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className={`${denseHeader ? 'px-0 pb-1.5 pt-0' : 'watchlist-panel px-3 pb-2.5 pt-2.5'}`}>
          <div className={`relative z-10 flex flex-wrap items-center justify-between ${denseHeader ? 'min-h-7 gap-1.5' : 'gap-2'}`}>
            <div className="min-w-0 flex flex-1 items-center gap-2">
              <div className={`watchlist-title inline-flex items-center text-[10px] font-semibold uppercase ${denseHeader ? 'tracking-[0.14em]' : 'tracking-[0.22em]'}`}>
                Watchlist
              </div>
            </div>

            <div className={`relative flex flex-wrap items-center ${denseHeader ? 'gap-1.5' : 'gap-2'}`}>
              <button
                type="button"
                title="Add instrument"
                onClick={() => setShowAddPanel(true)}
                className={`watchlist-control watchlist-control--primary no-drag inline-flex items-center justify-center border font-medium transition ${denseHeader ? 'h-7 w-7 rounded-md p-0' : 'gap-2 rounded-full px-3 py-2 text-[11px]'}`}
              >
                <Plus className="h-3.5 w-3.5" />
                <span className={denseHeader ? 'sr-only' : ''}>Add instrument</span>
              </button>

              {showLayoutControls && (
              <div className={`watchlist-control no-drag inline-flex items-center border p-0.5 ${denseHeader ? 'h-7 rounded-md' : 'rounded-full'}`}>
                {WATCHLIST_LAYOUT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleLayoutModeSelect(option.value)}
                    className={`${denseHeader ? 'rounded px-2 py-0.5 text-[9px] tracking-[0.08em]' : 'rounded-full px-2.5 py-1 text-[10px] tracking-[0.14em]'} watchlist-segment-button font-semibold uppercase transition ${requestedLayoutMode === option.value ? 'watchlist-segment-button--active' : ''}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              )}

            </div>
          </div>
        </div>

        <div
          ref={listAreaRef}
          className={`min-h-0 flex-1 overflow-auto ${denseHeader ? 'space-y-0 px-0 pb-0 pt-0' : 'space-y-2.5 px-3 pb-3 pt-2.5'}`}
          style={{ scrollbarGutter: 'auto' }}
        >
          {error && <div className="watchlist-error-box rounded-xl px-3 py-2 text-xs">{error}</div>}

          {loading && items.length === 0 && (
            <div className="watchlist-status-box rounded-xl px-3 py-3 text-xs">
              Syncing watchlist…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="watchlist-empty-box rounded-xl px-3 py-4 text-xs">
              No symbols yet. Use Add instrument above to drive the Studio symbol context from this panel.
            </div>
          )}

          <div
            ref={pillsRef}
            className={`watchlist-pills grid ${isFlatStyle ? 'gap-2.5' : (isVerticalLayout ? 'grid-cols-1 gap-2' : 'gap-2.5')}`}
            style={instrumentsLayoutStyle}
          >
            {isFlatStyle && isFlatHorizontalTiles ? items.map((item) => {
              const { symbol, quote, dailyPct, dailyPoints } = getInstrumentRenderData(item);
              return (
                <WatchlistCompactTile
                  key={item.id}
                  accountId={accountId}
                  symbol={symbol}
                  price={quote.bid}
                  deltaPct={dailyPct}
                  deltaPts={dailyPoints}
                  isActive={normalizeSymbol(activeSymbol) === normalizeSymbol(symbol)}
                  onClick={() => setTradeContext({ symbol })}
                  onEdit={() => beginEdit(item)}
                  onRemove={() => removeItem(item.id)}
                />
              );
            }) : isFlatStyle ? flatSections.map((sectionItems, sectionIndex) => (
              <div key={`flat-section-${sectionIndex}`} className={`watchlist-flat-list ${isFlushFlat ? 'watchlist-flat-list--flush' : ''}`}>
                <div className="watchlist-flat-header watchlist-flat-grid grid h-7 items-center gap-2 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-textSecondary">
                  {['Symbol', 'Last', 'Chg', 'Chg%'].map((headerLabel, columnIndex) => (
                    <span
                      key={headerLabel}
                      className={`watchlist-flat-header-cell relative min-w-0 ${columnIndex === 0 ? 'text-left' : 'text-right'}`}
                    >
                      <span className="truncate">{headerLabel}</span>
                      {columnIndex < 3 && (
                        <span
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${headerLabel} column`}
                          tabIndex={0}
                          className="watchlist-flat-resizer no-drag"
                          onPointerDown={(event) => startFlatColumnResize(columnIndex, event)}
                        />
                      )}
                    </span>
                  ))}
                  <span aria-hidden />
                </div>

                {sectionItems.map((item) => {
                  const { symbol, quote, dailyPct, dailyPoints } = getInstrumentRenderData(item);
                  return (
                    <WatchlistFlatRow
                      key={item.id}
                      accountId={accountId}
                      symbol={symbol}
                      price={quote.bid}
                      deltaPct={dailyPct}
                      deltaPts={dailyPoints}
                      isActive={normalizeSymbol(activeSymbol) === normalizeSymbol(symbol)}
                      onClick={() => setTradeContext({ symbol })}
                      onEdit={() => beginEdit(item)}
                      onRemove={() => removeItem(item.id)}
                    />
                  );
                })}
              </div>
            )) : items.map((item) => {
              const { symbol, quote, dailyPct, dailyPoints } = getInstrumentRenderData(item);
              return (
                <WatchlistPill3D
                  key={item.id}
                  accountId={accountId}
                  symbol={symbol}
                  price={quote.bid}
                  deltaPct={dailyPct}
                  deltaPts={dailyPoints}
                  isActive={normalizeSymbol(activeSymbol) === normalizeSymbol(symbol)}
                  layoutVariant={isVerticalLayout ? 'vertical' : 'horizontal'}
                  onClick={() => setTradeContext({ symbol })}
                  onEdit={() => beginEdit(item)}
                  onRemove={() => removeItem(item.id)}
                />
              );
            })}
          </div>

          {items.map((item) =>
            editingId === item.id ? (
              <div key={`edit-${item.id}`} className="watchlist-edit-panel rounded-[22px] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="watchlist-title text-[11px] font-semibold uppercase tracking-[0.22em]">Link settings</div>
                    <div className="mt-1 text-sm font-medium text-textPrimary">{formatDisplaySymbol(item.instrument)}</div>
                    <div className="mt-1 text-xs text-textSecondary">Choose where this symbol should appear.</div>
                  </div>
                  <button
                    type="button"
                    className="watchlist-icon-button rounded-full border p-1 transition"
                    onClick={() => { setEditingId(null); setSelectedAccountIds([]); }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="mt-4 grid max-h-44 grid-cols-1 gap-2 overflow-y-auto pr-1">
                  {accounts.map((account) => (
                    <label key={account.id} className="watchlist-checkbox-row flex items-center gap-2 rounded-xl px-3 py-2 text-xs">
                      <input
                        type="checkbox"
                        className="accent-cyan-500"
                        checked={selectedAccountIds.includes(account.id)}
                        onChange={() =>
                          setSelectedAccountIds((currentValue) =>
                            currentValue.includes(account.id)
                              ? currentValue.filter((selectedId) => selectedId !== account.id)
                              : [...currentValue, account.id]
                          )
                        }
                      />
                      <span className="truncate">{account.name}</span>
                    </label>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        await makeItemGlobal(item.id);
                        setEditingId(null);
                        setSelectedAccountIds([]);
                        await loadItems();
                      } catch (e) {
                        setError(e?.message || 'Failed to make global');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <Globe2 className="mr-1 h-3.5 w-3.5" />
                    Make Global
                  </Button>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setEditingId(null); setSelectedAccountIds([]); }}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveEdit}>
                      Save links
                    </Button>
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>

      {showAddPanel && typeof document !== 'undefined' && createPortal(
        <Modal
          open={showAddPanel}
          onClose={closeAddPanel}
          title="Add instrument"
          maxWidthClass="max-w-md"
          panelClassName="!p-4 text-textPrimary sm:!p-5"
          contentClassName="overflow-visible"
          footer={(
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={closeAddPanel}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={addItem} disabled={loading || !instrument}>
                Add symbol
              </Button>
            </>
          )}
        >
          <div className="space-y-3">
            <p className="text-xs text-textSecondary">Create a Studio watchlist card and choose which accounts it follows.</p>

            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-textSecondary">Instrument</label>
              <Input
                placeholder="Instrument"
                value={instrument}
                onChange={(event) => setInstrument(event.target.value)}
                className="h-9"
              />
            </div>

            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-textSecondary">Link coverage</div>
              <div className="mt-2 grid max-h-40 grid-cols-1 gap-1.5 overflow-y-auto pr-1">
                {accounts.map((account) => (
                  <label key={account.id} className="watchlist-checkbox-row flex items-center gap-2 rounded-lg px-3 py-2 text-xs">
                    <input
                      type="checkbox"
                      className="accent-cyan-500"
                      checked={selectedAccountIds.includes(account.id)}
                      onChange={() => {
                        setSelectedAccountIds((currentValue) =>
                          currentValue.includes(account.id)
                            ? currentValue.filter((selectedId) => selectedId !== account.id)
                            : [...currentValue, account.id]
                        );
                      }}
                    />
                    <span className="truncate">{account.name}</span>
                  </label>
                ))}
                {accounts.length === 0 && <div className="text-xs text-textSecondary">No accounts available</div>}
              </div>
            </div>
          </div>
        </Modal>,
        document.body
      )}
    </div>
  );
}
